import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { 
  FileText, 
  Upload, 
  Trash2, 
  Calendar,
  FolderOpen,
  Loader2,
  Plus,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { id: 'juridique', label: 'Juridique' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'marketing', label: 'Marketing' },
  { id: 'operationnel', label: 'Opérationnel' },
  { id: 'rh', label: 'RH' },
];

const categoryColors = {
  juridique: 'category-juridique',
  commercial: 'category-commercial',
  marketing: 'category-marketing',
  operationnel: 'category-operationnel',
  rh: 'bg-teal-100 text-teal-700',
};

export const AdminDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [newDoc, setNewDoc] = useState({
    name: '',
    category: '',
    file: null,
    isAvUrssaf: false,
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await fetch(`${API}/documents`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setNewDoc(prev => ({ ...prev, file, name: prev.name || file.name.replace(/\.[^/.]+$/, '') }));
    }
  };

  const uploadDocument = async () => {
    if (!newDoc.name || !newDoc.category || !newDoc.file) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setUploading(true);
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target?.result?.toString().split(',')[1];
        const fileType = newDoc.file.name.split('.').pop();

        const response = await fetch(`${API}/documents`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            name: newDoc.name,
            category: newDoc.category,
            file_data: base64,
            file_type: fileType,
            is_av_urssaf: newDoc.isAvUrssaf,
          }),
        });

        if (response.ok) {
          toast.success('Document uploadé');
          setIsDialogOpen(false);
          setNewDoc({ name: '', category: '', file: null, isAvUrssaf: false });
          if (fileInputRef.current) fileInputRef.current.value = '';
          fetchDocuments();
        } else {
          const err = await response.json().catch(() => ({}));
          toast.error(err.detail || 'Erreur lors de l\'upload');
        }
        setUploading(false);
      };
      reader.readAsDataURL(newDoc.file);
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error('Erreur lors de l\'upload');
      setUploading(false);
    }
  };

  const deleteDocument = async (documentId) => {
    try {
      const response = await fetch(`${API}/documents/${documentId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('Document supprimé');
        setDocuments(prev => prev.filter(d => d.document_id !== documentId));
      }
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white font-[Manrope]" data-testid="admin-documents-title">
            Gérer les documents
          </h1>
          <p className="text-slate-500 mt-1">Uploadez et gérez les documents partagés</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="upload-document-btn">
              <Plus className="w-4 h-4 mr-2" />
              Uploader un document
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nouveau document</DialogTitle>
              <DialogDescription>
                Uploadez un document à partager avec l'équipe commerciale
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Fichier *</Label>
                <Input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt"
                  data-testid="file-input"
                />
                {newDoc.file && (
                  <p className="text-xs text-slate-500">
                    Fichier sélectionné: {newDoc.file.name}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Nom du document *</Label>
                <Input
                  value={newDoc.name}
                  onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })}
                  placeholder="Grille tarifaire 2025"
                  data-testid="doc-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Catégorie *</Label>
                <Select
                  value={newDoc.category}
                  onValueChange={(value) => setNewDoc({ ...newDoc, category: value })}
                >
                  <SelectTrigger data-testid="doc-category-select">
                    <SelectValue placeholder="Sélectionner une catégorie" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Checkbox
                  id="av-urssaf"
                  checked={newDoc.isAvUrssaf}
                  onCheckedChange={(checked) => setNewDoc({ ...newDoc, isAvUrssaf: checked })}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor="av-urssaf"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                  >
                    <Shield className="w-4 h-4 text-blue-600" />
                    D_AV_URSSAF
                  </label>
                  <p className="text-xs text-slate-500">
                    Document d'aide pour obtenir l'attestation de vigilance (max 3)
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annuler
              </Button>
              <Button 
                onClick={uploadDocument} 
                disabled={uploading}
                data-testid="submit-upload-btn"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Uploader
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {categories.map((cat) => (
          <Card key={cat.id} className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`p-3 rounded-xl ${categoryColors[cat.id]?.split(' ')[0] || 'bg-slate-100'}`}>
                <FolderOpen className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-slate-500">{cat.label}</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  {documents.filter(d => d.category === cat.id).length}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Documents table */}
      <Card className="border-0 shadow-sm overflow-hidden" data-testid="documents-table-card">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-900">
                <TableHead className="font-semibold">Document</TableHead>
                <TableHead className="font-semibold">Catégorie</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Mis à jour</TableHead>
                <TableHead className="font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    Chargement...
                  </TableCell>
                </TableRow>
              ) : documents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                    Aucun document
                  </TableCell>
                </TableRow>
              ) : (
                documents.map((doc, index) => (
                  <TableRow 
                    key={doc.document_id}
                    className="table-row-hover animate-fade-in"
                    style={{ animationDelay: `${index * 0.02}s` }}
                    data-testid={`doc-row-${doc.document_id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-slate-100 dark:bg-slate-800">
                          <FileText className="w-5 h-5 text-slate-600" />
                        </div>
                        <span className="font-medium">{doc.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge className={`${categoryColors[doc.category]} border`}>
                          {doc.category}
                        </Badge>
                        {doc.is_av_urssaf && (
                          <Badge className="bg-blue-100 text-blue-700 border border-blue-300">
                            <Shield className="w-3 h-3 mr-1" />
                            AV_URSSAF
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="uppercase text-slate-500 text-sm">
                      {doc.file_type}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {new Date(doc.updated_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-rose-500 hover:text-rose-600 hover:bg-rose-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Cette action est irréversible. Le document "{doc.name}" sera définitivement supprimé.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteDocument(doc.document_id)}
                              className="bg-rose-600 hover:bg-rose-700"
                            >
                              Supprimer
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};
